import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Package, GraduationCap, FileText, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const EMPTY = { products: [], formations: [], articles: [] };

export function SearchOverlay({ open, setOpen }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setResults(EMPTY);
      setTimeout(() => inputRef.current?.focus(), 120);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  const onChange = (val) => {
    setQ(val);
    clearTimeout(timer.current);
    if (val.trim().length < 2) { setResults(EMPTY); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(val)}`);
        setResults(data);
      } catch { setResults(EMPTY); }
      setLoading(false);
    }, 300);
  };

  const close = () => setOpen(false);
  const total = results.products.length + results.formations.length + results.articles.length;

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-start justify-center px-4 pt-24"
          style={{ background: "rgba(15,17,21,0.6)", backdropFilter: "blur(6px)" }}
          onClick={close} data-testid="search-overlay">
          <motion.div initial={{ y: -16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -16, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-5 py-4" style={{ background: "var(--bone)", border: "1px solid var(--border)" }}>
              <Search size={20} style={{ color: "var(--orange)" }} />
              <input ref={inputRef} value={q} onChange={(e) => onChange(e.target.value)}
                placeholder="Rechercher un produit, une formation, un conseil…"
                className="flex-1 bg-transparent outline-none text-lg" data-testid="global-search-input" />
              {loading && <Loader2 size={18} className="animate-spin" style={{ color: "var(--ink-2)" }} />}
              <button onClick={close} data-testid="search-close-btn"><X size={20} /></button>
            </div>

            {q.trim().length >= 2 && !loading && (
              <div className="max-h-[60vh] overflow-y-auto" style={{ background: "var(--white)", border: "1px solid var(--border)", borderTop: "none" }} data-lenis-prevent data-testid="search-results">
                {total === 0 && <p className="p-6 text-sm" style={{ color: "var(--ink-2)" }}>Aucun résultat pour « {q} ».</p>}
                {results.products.length > 0 && (
                  <div className="p-4">
                    <p className="overline mb-2 flex items-center gap-2" style={{ color: "var(--orange)" }}><Package size={13} /> Produits</p>
                    {results.products.map((p) => (
                      <Link key={p.id} to={`/catalogue/${p.id}`} onClick={close} data-testid={`search-product-${p.id}`}
                        className="flex items-center gap-3 px-2 py-2 hover:bg-black/5 transition-colors duration-150">
                        <img src={p.image} alt="" className="w-10 h-10 object-cover shrink-0" style={{ border: "1px solid var(--border)" }} />
                        <span className="flex-1 text-sm font-medium truncate">{p.name}</span>
                        <span className="text-sm font-display font-bold whitespace-nowrap">Sur devis</span>
                      </Link>
                    ))}
                  </div>
                )}
                {results.formations.length > 0 && (
                  <div className="p-4" style={{ borderTop: "1px solid var(--border)" }}>
                    <p className="overline mb-2 flex items-center gap-2" style={{ color: "var(--orange)" }}><GraduationCap size={13} /> Formations</p>
                    {results.formations.map((f) => (
                      <Link key={f.id} to={`/formations/${f.id}`} onClick={close} data-testid={`search-formation-${f.id}`}
                        className="flex items-center gap-3 px-2 py-2 hover:bg-black/5 transition-colors duration-150">
                        <span className="flex-1 text-sm font-medium truncate">{f.title}</span>
                        <span className="text-xs whitespace-nowrap" style={{ color: "var(--ink-2)" }}>{f.domain}</span>
                      </Link>
                    ))}
                  </div>
                )}
                {results.articles.length > 0 && (
                  <div className="p-4" style={{ borderTop: "1px solid var(--border)" }}>
                    <p className="overline mb-2 flex items-center gap-2" style={{ color: "var(--orange)" }}><FileText size={13} /> Conseils</p>
                    {results.articles.map((a) => (
                      <Link key={a.id} to={`/conseils/${a.slug}`} onClick={close} data-testid={`search-article-${a.id}`}
                        className="flex items-center gap-3 px-2 py-2 hover:bg-black/5 transition-colors duration-150">
                        <span className="flex-1 text-sm font-medium truncate">{a.title}</span>
                        <span className="text-xs whitespace-nowrap" style={{ color: "var(--ink-2)" }}>{a.category}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
